import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FiscalYearProvider } from "@/context/FiscalYearContext";
import Layout from "@/components/Layout";
import PlatformLayout from "@/components/PlatformLayout";
import Login from "@/pages/Login";
import LandingPage from "@/pages/LandingPage";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import StaffHome from "@/pages/StaffHome";
import Trainings from "@/pages/Trainings";
import TrainingDetail from "@/pages/TrainingDetail";
import TrainingPlayer from "@/pages/TrainingPlayer";
import AnnualPlan from "@/pages/AnnualPlan";
import Committees from "@/pages/Committees";
import Drills from "@/pages/Drills";
import Staff from "@/pages/Staff";
import Progress from "@/pages/Progress";
import Certificates from "@/pages/Certificates";
import Reports from "@/pages/Reports";
import Notifications from "@/pages/Notifications";
import Messages from "@/pages/Messages";
import DeliveryReport from "@/pages/DeliveryReport";
import Onboarding from "@/pages/Onboarding";
import OnboardingDetail from "@/pages/onboarding/OnboardingDetail";
import OnboardingSettings from "@/pages/onboarding/OnboardingSettings";
import Offices from "@/pages/Offices";
import AuditMode from "@/pages/AuditMode";
import AuditLogs from "@/pages/AuditLogs";
import Billing from "@/pages/Billing";
import PaymentResult from "@/pages/PaymentResult";
import Settings from "@/pages/Settings";
import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import PlatformCompanies from "@/pages/platform/PlatformCompanies";
import PlatformPlans from "@/pages/platform/PlatformPlans";
import PlatformMasterTrainings from "@/pages/platform/PlatformMasterTrainings";
import PlatformAnnualPlan from "@/pages/platform/PlatformAnnualPlan";
import PlatformCertificate from "@/pages/platform/PlatformCertificate";
import PlatformContacts from "@/pages/platform/PlatformContacts";
import PlatformLPSettings from "@/pages/platform/PlatformLPSettings";
import Verify from "@/pages/Verify";
import UserGuide from "@/pages/UserGuide";

function FullLoading() {
  return (
    <div className="h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );
}

function RoleHome() {
  const { user } = useAuth();
  return user.role === "staff" ? <StaffHome /> : <Dashboard />;
}

function PlatformRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route path="/verify/:certNo" element={<Verify />} />
      <Route path="/guide" element={<UserGuide />} />
      <Route element={<PlatformLayout />}>
        <Route path="/" element={<PlatformDashboard />} />
        <Route path="/platform/companies" element={<PlatformCompanies />} />
        <Route path="/platform/plans" element={<PlatformPlans />} />
        <Route path="/platform/master" element={<PlatformMasterTrainings />} />
        <Route path="/platform/annual-plan" element={<PlatformAnnualPlan />} />
        <Route path="/platform/certificate" element={<PlatformCertificate />} />
        <Route path="/platform/lp-settings" element={<PlatformLPSettings />} />
        <Route path="/platform/contacts" element={<PlatformContacts />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function TenantRoutes() {
  return (
    <FiscalYearProvider>
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route element={<Layout />}>
          <Route path="/" element={<RoleHome />} />
          <Route path="/trainings" element={<Trainings />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/onboarding/:userId" element={<OnboardingDetail />} />
          <Route path="/onboarding-settings" element={<OnboardingSettings />} />
          <Route path="/trainings/:id" element={<TrainingDetail />} />
          <Route path="/learn/:id" element={<TrainingPlayer />} />
          <Route path="/annual-plan" element={<AnnualPlan />} />
          <Route path="/committees" element={<Committees />} />
          <Route path="/drills" element={<Drills />} />
          <Route path="/staff" element={<Staff />} />
          <Route path="/progress" element={<Progress />} />
          <Route path="/certificates" element={<Certificates />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/notifications" element={<Notifications />} />
          <Route path="/mail" element={<Messages />} />
          <Route path="/delivery-report" element={<DeliveryReport />} />
          <Route path="/offices" element={<Offices />} />
          <Route path="/audit-mode" element={<AuditMode />} />
          <Route path="/audit-logs" element={<AuditLogs />} />
          <Route path="/billing" element={<Billing />} />
          <Route path="/payment/success" element={<PaymentResult />} />
          <Route path="/payment/cancel" element={<PaymentResult />} />
          <Route path="/settings" element={<Settings />} />
        </Route>
        <Route path="/verify/:certNo" element={<Verify />} />
        <Route path="/guide" element={<UserGuide />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </FiscalYearProvider>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  if (user === null) return <FullLoading />;
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/guide" element={<UserGuide />} />
        <Route path="/verify/:certNo" element={<Verify />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }
  return user.role === "super_admin" ? <PlatformRoutes /> : <TenantRoutes />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
